/**
 * Barcode Validation & ISO/IEC Checksum Engine
 * Supports EAN-13, UPC-A, Code-128, GS1-DataMatrix, and Micro QR Code.
 */

export interface ValidationResult {
  isValid: boolean;
  message: string;
  symbology: string;
  originalCode: string;
  repairedCode?: string;
  expectedChecksum?: number;
  actualChecksum?: number;
  details?: string;
  metadata?: Record<string, any>;
}

/**
 * Modulo 10 Checksum calculation for EAN-13
 * Base MUST be 12 numeric digits.
 */
export function calculateEan13Checksum(base12: string): number {
  const digits = base12.replace(/\D/g, '');
  if (digits.length !== 12) return 0;

  let sum = 0;
  for (let i = 0; i < 12; i++) {
    const digit = parseInt(digits[i], 10);
    sum += i % 2 === 0 ? digit * 1 : digit * 3;
  }
  return (10 - (sum % 10)) % 10;
}

/**
 * Validates EAN-13 code and provides auto-repaired code if checksum is wrong.
 */
export function validateEan13(code: string): ValidationResult {
  const raw = String(code || '').trim();
  const digits = raw.replace(/\D/g, '');

  if (!digits) {
    return {
      isValid: false,
      symbology: 'EAN-13',
      originalCode: raw,
      message: 'Code is empty or contains no numeric digits.'
    };
  }

  if (digits.length === 12) {
    const checksum = calculateEan13Checksum(digits);
    const repaired = digits + checksum;
    return {
      isValid: true,
      symbology: 'EAN-13',
      originalCode: raw,
      repairedCode: repaired,
      expectedChecksum: checksum,
      actualChecksum: checksum,
      message: `Valid 12-digit GTIN base. Auto-appended checksum digit ${checksum} to form 13-digit EAN-13 (${repaired}).`
    };
  }

  if (digits.length === 13) {
    const base12 = digits.slice(0, 12);
    const actualChecksum = parseInt(digits[12], 10);
    const expectedChecksum = calculateEan13Checksum(base12);
    const isValid = actualChecksum === expectedChecksum;
    const repaired = base12 + expectedChecksum;

    return {
      isValid,
      symbology: 'EAN-13',
      originalCode: raw,
      repairedCode: repaired,
      expectedChecksum,
      actualChecksum,
      message: isValid
        ? `Valid EAN-13 barcode with correct ISO/IEC Modulo-10 checksum (${actualChecksum}).`
        : `Invalid Modulo-10 Checksum! Expected ${expectedChecksum}, but found ${actualChecksum}. Repaired EAN-13 is ${repaired}.`
    };
  }

  return {
    isValid: false,
    symbology: 'EAN-13',
    originalCode: raw,
    message: `Invalid digit length (${digits.length} digits). EAN-13 requires exactly 12 or 13 numeric digits.`
  };
}

/**
 * Modulo 10 Checksum calculation for UPC-A
 * Base MUST be 11 numeric digits.
 */
export function calculateUpcaChecksum(base11: string): number {
  const digits = base11.replace(/\D/g, '');
  if (digits.length !== 11) return 0;

  let sum = 0;
  for (let i = 0; i < 11; i++) {
    const digit = parseInt(digits[i], 10);
    sum += i % 2 === 0 ? digit * 3 : digit * 1;
  }
  return (10 - (sum % 10)) % 10;
}

/**
 * Validates UPC-A code.
 */
export function validateUpca(code: string): ValidationResult {
  const raw = String(code || '').trim();
  const digits = raw.replace(/\D/g, '');

  if (!digits) {
    return {
      isValid: false,
      symbology: 'UPC-A',
      originalCode: raw,
      message: 'Code is empty or contains no numeric digits.'
    };
  }

  if (digits.length === 11) {
    const checksum = calculateUpcaChecksum(digits);
    const repaired = digits + checksum;
    return {
      isValid: true,
      symbology: 'UPC-A',
      originalCode: raw,
      repairedCode: repaired,
      expectedChecksum: checksum,
      actualChecksum: checksum,
      message: `Valid 11-digit UPC base. Auto-appended checksum digit ${checksum} to form 12-digit UPC-A (${repaired}).`
    };
  }

  if (digits.length === 12) {
    const base11 = digits.slice(0, 11);
    const actualChecksum = parseInt(digits[11], 10);
    const expectedChecksum = calculateUpcaChecksum(base11);
    const isValid = actualChecksum === expectedChecksum;
    const repaired = base11 + expectedChecksum;

    return {
      isValid,
      symbology: 'UPC-A',
      originalCode: raw,
      repairedCode: repaired,
      expectedChecksum,
      actualChecksum,
      message: isValid
        ? `Valid UPC-A barcode with correct Modulo-10 checksum (${actualChecksum}).`
        : `Invalid Modulo-10 Checksum! Expected ${expectedChecksum}, but found ${actualChecksum}. Repaired UPC-A is ${repaired}.`
    };
  }

  return {
    isValid: false,
    symbology: 'UPC-A',
    originalCode: raw,
    message: `Invalid digit length (${digits.length} digits). UPC-A requires exactly 11 or 12 numeric digits.`
  };
}

/**
 * Validates Code-128 code.
 */
export function validateCode128(code: string): ValidationResult {
  const raw = String(code || '').trim();
  if (!raw) {
    return {
      isValid: false,
      symbology: 'Code-128',
      originalCode: raw,
      message: 'Code-128 string cannot be empty.'
    };
  }

  // Check ASCII range 32 - 126
  let isValid = true;
  for (let i = 0; i < raw.length; i++) {
    const charCode = raw.charCodeAt(i);
    if (charCode < 32 || charCode > 126) {
      isValid = false;
      break;
    }
  }

  return {
    isValid,
    symbology: 'Code-128',
    originalCode: raw,
    message: isValid
      ? `Valid Code-128 string (${raw.length} printable ASCII characters).`
      : 'Contains non-standard ASCII characters outside printable range (0x20 - 0x7E).'
  };
}

/**
 * GS1 Application Identifiers Parser & Validator
 */
export interface GS1AI {
  ai: string;
  label: string;
  value: string;
}

export function validateGs1DataMatrix(code: string): ValidationResult {
  const raw = String(code || '').trim();
  if (!raw) {
    return {
      isValid: false,
      symbology: 'GS1-DataMatrix',
      originalCode: raw,
      message: 'GS1 string is empty.'
    };
  }

  const aiRegex = /\((01|10|11|17|21|30|310\d|320\d|90|91|92|93)\)([^\(]+)/g;
  const parsedAIs: GS1AI[] = [];
  let match: RegExpExecArray | null;

  const aiLabels: Record<string, string> = {
    '01': 'GTIN (Global Trade Item Number)',
    '10': 'Batch / Lot Number',
    '11': 'Production Date (YYMMDD)',
    '17': 'Expiration Date (YYMMDD)',
    '21': 'Serial Number',
    '30': 'Count / Quantity',
    '90': 'Company Internal Data'
  };

  while ((match = aiRegex.exec(raw)) !== null) {
    const ai = match[1];
    const val = match[2].trim();
    parsedAIs.push({
      ai,
      label: aiLabels[ai] || `AI (${ai})`,
      value: val
    });
  }

  let message = '';
  let isValid = true;
  let repairedCode = raw;

  if (parsedAIs.length > 0) {
    message = `Detected ${parsedAIs.length} GS1 Application Identifiers: ${parsedAIs.map(a => `(${a.ai}) ${a.label}`).join(', ')}.`;

    // If GTIN (01) is present, check GTIN checksum
    const gtinAI = parsedAIs.find(a => a.ai === '01');
    if (gtinAI) {
      const gtinDigits = gtinAI.value.replace(/\D/g, '');
      if (gtinDigits.length === 14) {
        const base13 = gtinDigits.slice(0, 13);
        const actualCheck = parseInt(gtinDigits[13], 10);
        
        let sum = 0;
        for (let i = 0; i < 13; i++) {
          const digit = parseInt(base13[i], 10);
          sum += i % 2 === 0 ? digit * 3 : digit * 1;
        }
        const expectedCheck = (10 - (sum % 10)) % 10;

        if (actualCheck !== expectedCheck) {
          isValid = false;
          message += ` GTIN-14 Checksum mismatch! Expected ${expectedCheck}, got ${actualCheck}.`;
          const repairedGtin = base13 + expectedCheck;
          repairedCode = raw.replace(`(01)${gtinAI.value}`, `(01)${repairedGtin}`);
        } else {
          message += ' GTIN-14 Modulo-10 checksum is verified.';
        }
      }
    }
  } else {
    // If raw doesn't have (XX) AI parentheses, assume standard string or raw FNC1
    isValid = true;
    message = 'Standard DataMatrix string. Tip: Use (01)GTIN(10)LOT(17)YYMMDD for GS1 ISO compliance.';
  }

  return {
    isValid,
    symbology: 'GS1-DataMatrix',
    originalCode: raw,
    repairedCode,
    message,
    metadata: { parsedAIs }
  };
}

/**
 * Micro QR Code Validator
 */
export function validateMicroQr(code: string): ValidationResult {
  const raw = String(code || '').trim();
  if (!raw) {
    return {
      isValid: false,
      symbology: 'Micro QR',
      originalCode: raw,
      message: 'Micro QR content is empty.'
    };
  }

  const len = raw.length;
  const isNumericOnly = /^\d+$/.test(raw);
  let recommendedVersion = 'M1';

  if (isNumericOnly) {
    if (len <= 5) recommendedVersion = 'M1 (5 digits max)';
    else if (len <= 10) recommendedVersion = 'M2 (10 digits max)';
    else if (len <= 23) recommendedVersion = 'M3 (23 digits max)';
    else if (len <= 35) recommendedVersion = 'M4 (35 digits max)';
    else {
      return {
        isValid: false,
        symbology: 'Micro QR',
        originalCode: raw,
        message: `Numeric payload length (${len}) exceeds maximum Micro QR M4 capacity (35 digits). Use standard QR code instead.`
      };
    }
  } else {
    if (len <= 6) recommendedVersion = 'M2 (6 alphanumeric max)';
    else if (len <= 14) recommendedVersion = 'M3 (14 alphanumeric max)';
    else if (len <= 21) recommendedVersion = 'M4 (21 alphanumeric max)';
    else {
      return {
        isValid: false,
        symbology: 'Micro QR',
        originalCode: raw,
        message: `Payload length (${len}) exceeds maximum Micro QR M4 capacity (21 alphanumeric characters). Use standard QR code instead.`
      };
    }
  }

  return {
    isValid: true,
    symbology: 'Micro QR',
    originalCode: raw,
    message: `Valid Micro QR payload. Recommended matrix version: ${recommendedVersion}.`
  };
}

/**
 * Universal Barcode Validator Router
 */
export function validateBarcode(code: string, symbology: string): ValidationResult {
  const sym = (symbology || '').toLowerCase();
  if (sym === 'ean13') return validateEan13(code);
  if (sym === 'upca') return validateUpca(code);
  if (sym === 'code128' || sym === 'barcode') return validateCode128(code);
  if (sym === 'gs1datamatrix' || sym === 'datamatrix') return validateGs1DataMatrix(code);
  if (sym === 'microqr') return validateMicroQr(code);

  return validateCode128(code);
}

/**
 * Validates a dataset column of barcodes and provides summary & repair routines
 */
export function validateBarcodeColumn(
  rows: Record<string, any>[],
  columnName: string,
  symbology: string
) {
  const results: ValidationResult[] = [];
  let validCount = 0;
  let invalidCount = 0;

  for (let i = 0; i < rows.length; i++) {
    const val = String(rows[i][columnName] || '');
    const res = validateBarcode(val, symbology);
    results.push(res);
    if (res.isValid) validCount++;
    else invalidCount++;
  }

  return {
    columnName,
    symbology,
    totalRows: rows.length,
    validCount,
    invalidCount,
    complianceScore: rows.length > 0 ? Math.round((validCount / rows.length) * 100) : 100,
    results
  };
}

/**
 * Repairs all checksums in a dataset column
 */
export function repairBarcodeDatasetColumn(
  rows: Record<string, any>[],
  columnName: string,
  symbology: string
): { repairedRows: Record<string, any>[]; repairedCount: number } {
  let repairedCount = 0;
  const repairedRows = rows.map(r => ({ ...r }));

  for (let i = 0; i < repairedRows.length; i++) {
    const val = String(repairedRows[i][columnName] || '');
    const res = validateBarcode(val, symbology);
    if (!res.isValid && res.repairedCode) {
      repairedRows[i][columnName] = res.repairedCode;
      repairedCount++;
    }
  }

  return { repairedRows, repairedCount };
}
